import type { JSX } from 'react';
import { useState } from 'react';
import type { WireSourceInstance } from '@logistics/contracts';
import { failureFrom } from '@/lib/result';
import type { Me } from '../auth/use-me';
import { useSourceHealth } from '../health/use-source-health';
import { button } from '../shell/look';
import { PageHead } from '../shell/page-head';
import type { Route } from '../shell/routes';
import { ErrorState, LoadingState } from '../shell/states';
import { useToast } from '../shell/toast';
import { navigate } from '../shell/use-route';
import { ConnectAnother, SourceCard } from './source-card';
import { SourceModal } from './source-modal';
import { SourcePanel } from './source-panel';
import { useInstances, useKinds, useRefreshSources } from './use-sources';

const HOME: Route = { page: 'sources' };

/**
 * Connected sources (design L362–391). `#/sources/add` opens the catalog,
 * `#/sources/<instanceId>` the detail drawer; only an admin may change anything.
 */
export function SourcesPage({ route, me }: { route: Route; me: Me }): JSX.Element {
  const admin = me.role === 'admin';
  const kinds = useKinds();
  const instances = useInstances();
  const health = useSourceHealth();
  const refresh = useRefreshSources();
  const toast = useToast();
  const [editing, setEditing] = useState<WireSourceInstance | undefined>(undefined);

  const add = (): void => navigate({ page: 'sources', action: 'add' });
  const descriptorOf = (kind: string) => kinds.data?.find((d) => d.kind === kind);
  const healthOf = (id: string) => health.data?.find((row) => row.instanceId === id);
  const selected = instances.data?.find((i) => i.id === route.action);
  const saved = (message: string): void => {
    toast(message);
    setEditing(undefined);
    navigate(HOME);
    void refresh();
  };

  return (
    <section className="animate-rise">
      <PageHead
        eyebrow="RETRIEVAL SOURCES"
        title="Connected sources"
        description="Everything the agent can read from. Credentials are encrypted at rest and never shown again once saved."
        action={
          admin ? (
            <button type="button" onClick={add} className={button.primary}>
              + Add source
            </button>
          ) : undefined
        }
      />
      <Grid admin={admin} onAdd={add} />
      {selected !== undefined && (
        <SourcePanel
          key={selected.id}
          instance={selected}
          descriptor={descriptorOf(selected.kind)}
          health={healthOf(selected.id)}
          admin={admin}
          onClose={() => navigate(HOME)}
          onEdit={() => {
            setEditing(selected);
            navigate(HOME);
          }}
        />
      )}
      {admin && kinds.data !== undefined && (route.action === 'add' || editing !== undefined) && (
        <SourceModal
          key={editing?.id ?? 'add'}
          descriptors={kinds.data}
          editing={editing}
          onSaved={saved}
          onClose={() => {
            setEditing(undefined);
            navigate(HOME);
          }}
        />
      )}
    </section>
  );
}

/** The card grid, with the dashed "connect another" tile for an admin. */
function Grid({ admin, onAdd }: { admin: boolean; onAdd: () => void }): JSX.Element {
  const kinds = useKinds();
  const instances = useInstances();
  const health = useSourceHealth();
  const failed = kinds.error ?? instances.error;

  if (failed !== null) {
    return (
      <ErrorState
        {...failureFrom(failed)}
        onRetry={() => void Promise.all([kinds.refetch(), instances.refetch()])}
      />
    );
  }
  if (instances.data === undefined || kinds.data === undefined) return <LoadingState rows={4} />;
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5">
      {instances.data.map((instance) => (
        <SourceCard
          key={instance.id}
          instance={instance}
          descriptor={kinds.data.find((d) => d.kind === instance.kind)}
          health={health.data?.find((row) => row.instanceId === instance.id)}
          onOpen={() => navigate({ page: 'sources', action: instance.id })}
        />
      ))}
      {admin && <ConnectAnother onClick={onAdd} />}
    </div>
  );
}
